import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Card, Row, Col, Form, Button, Spinner, Alert,
    InputGroup, Badge, Tab, Tabs
} from 'react-bootstrap';
import {
    Building2, Globe, Palette, Save, Upload,
    Hash, Mail, Phone, MapPin, RefreshCw, CheckCircle, Shield
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
// FIX: Correct relative import — both files are in frontend/src/views/settings/
import CompanyPayrollSettings from './../settings/CompanyPayrollSettings';

const BrandingSettings = () => {
    const { user, setUser } = useAuth();
    const logoRef = useRef(null);
    const [loading,  setLoading]  = useState(true);
    const [saving,   setSaving]   = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [preview,  setPreview]  = useState(null);
    const [saved,    setSaved]    = useState(false);
    const [form, setForm] = useState({
        name:           '',
        email:          '',
        phone:          '',
        address:        '',
        website:        '',
        company_code:   '',
        company_domain: '',
        primary_color:  '#4f46e5',
        secondary_color:'#7c3aed',
        tagline:        '',
    });

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/companies/my');
            const c   = res.data.data || {};
            setForm({
                name:           c.name           || '',
                email:          c.email          || '',
                phone:          c.phone          || '',
                address:        c.address        || '',
                website:        c.website        || '',
                company_code:   c.company_code   || '',
                company_domain: c.company_domain || '',
                primary_color:  c.primary_color  || '#4f46e5',
                secondary_color:c.secondary_color|| '#7c3aed',
                tagline:        c.tagline        || '',
            });
            if (c.logo_url) setPreview(c.logo_url);
        } catch (err) {
            toast.error('Failed to load settings');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchSettings(); }, [fetchSettings]);

    const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
        setLogoFile(file);
        setPreview(URL.createObjectURL(file));
    };

    const handleSave = async (ev) => {
        ev.preventDefault();
        if (!form.name?.trim()) return toast.error('Company name is required');
        if (form.company_code && !/^[A-Za-z0-9]{1,6}$/.test(form.company_code))
            return toast.error('Company code must be 1–6 letters/numbers only (e.g. I2S)');
        if (form.company_domain && !/^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/.test(form.company_domain))
            return toast.error('Domain must be like: peoplesdock.com or company.com');

        setSaving(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v || ''));
            if (logoFile) fd.append('logo', logoFile);
            await axiosInstance.put('/companies/my', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            document.documentElement.style.setProperty('--primary-color', form.primary_color);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
            toast.success('Settings saved successfully');
            fetchSettings();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save settings');
        } finally { setSaving(false); }
    };

    if (loading) return (
        <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted small">Loading settings…</p>
        </div>
    );

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <Building2 size={22} className="me-2 text-primary" />
                        Organisation Settings
                    </h4>
                    <p className="text-muted small mb-0">
                        Configure your company profile, branding, HR automation and payroll settings
                    </p>
                </div>
                {saved && (
                    <Badge bg="success" className="d-flex align-items-center gap-1 px-3 py-2">
                        <CheckCircle size={14} /> Saved
                    </Badge>
                )}
            </div>

            {/* FIX: CompanyPayrollSettings is now a proper 4th Tab — cleaner UX
                The Payroll tab renders CompanyPayrollSettings which has its own save button.
                The main Form's Save button only applies to the General/Automation/Branding tabs. */}
            <Tabs defaultActiveKey="general" className="mb-4">

                {/* ── Tab 1: General / Company Info ── */}
                <Tab eventKey="general" title={<span><Building2 size={14} className="me-1" />General</span>}>
                    <Form noValidate onSubmit={handleSave}>
                        <Row className="g-4">
                            {/* Logo */}
                            <Col xs={12}>
                                <Card className="border-0 shadow-sm p-4">
                                    <h6 className="fw-bold mb-3">Company Logo</h6>
                                    <div className="d-flex align-items-center gap-4 flex-wrap">
                                        <div style={{
                                            width:80, height:80, borderRadius:12, flexShrink:0,
                                            background: preview ? 'transparent' : '#eef2ff',
                                            border: '2px dashed #c7d2fe',
                                            display:'flex', alignItems:'center', justifyContent:'center',
                                            overflow:'hidden',
                                        }}>
                                            {preview ? (
                                                <img
                                                    src={preview.startsWith('blob') ? preview
                                                        : preview.startsWith('http') ? preview
                                                        : `${(process.env.REACT_APP_API_URL||'').replace('/api/v1','')}${preview}`}
                                                    alt="Logo"
                                                    style={{ width:'100%', height:'100%', objectFit:'contain' }} />
                                            ) : (
                                                <Building2 size={28} className="text-primary opacity-50" />
                                            )}
                                        </div>
                                        <div>
                                            <Button variant="outline-primary" size="sm"
                                                onClick={() => logoRef.current?.click()}>
                                                <Upload size={14} className="me-1" />
                                                {preview ? 'Change Logo' : 'Upload Logo'}
                                            </Button>
                                            <Form.Control ref={logoRef} type="file"
                                                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                                                className="d-none" onChange={handleLogoChange} />
                                            <div className="text-muted small mt-1">PNG, JPG, SVG or WebP — max 2MB</div>
                                        </div>
                                    </div>
                                </Card>
                            </Col>

                            {/* Basic Info */}
                            <Col xs={12}>
                                <Card className="border-0 shadow-sm p-4">
                                    <h6 className="fw-bold mb-3">Basic Information</h6>
                                    <Row className="g-3">
                                        <Col xs={12} md={6}>
                                            <Form.Label className="small fw-bold">Company Name *</Form.Label>
                                            <Form.Control value={form.name}
                                                onChange={e => set('name', e.target.value)}
                                                placeholder="e.g. I2Speed Technologies LLP" required />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Label className="small fw-bold">Website</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text><Globe size={14} /></InputGroup.Text>
                                                <Form.Control value={form.website}
                                                    onChange={e => set('website', e.target.value)}
                                                    placeholder="https://peoplesdock.com" />
                                            </InputGroup>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Label className="small fw-bold">Business Email</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text><Mail size={14} /></InputGroup.Text>
                                                <Form.Control type="email" value={form.email}
                                                    onChange={e => set('email', e.target.value)}
                                                    placeholder="admin@peoplesdock.com" />
                                            </InputGroup>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Label className="small fw-bold">Phone</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text><Phone size={14} /></InputGroup.Text>
                                                <Form.Control value={form.phone}
                                                    onChange={e => set('phone', e.target.value)}
                                                    placeholder="+91 9611892030" />
                                            </InputGroup>
                                        </Col>
                                        <Col xs={12}>
                                            <Form.Label className="small fw-bold">Address</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text><MapPin size={14} /></InputGroup.Text>
                                                <Form.Control as="textarea" rows={2} value={form.address}
                                                    onChange={e => set('address', e.target.value)}
                                                    placeholder="123 Srinivasapuram, Tirupati, Andhra Pradesh - 517501" />
                                            </InputGroup>
                                        </Col>
                                        <Col xs={12}>
                                            <Form.Label className="small fw-bold">Tagline / Description</Form.Label>
                                            <Form.Control value={form.tagline}
                                                onChange={e => set('tagline', e.target.value)}
                                                placeholder="e.g. Empowering People, Driving Growth" />
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button variant="light" type="button" onClick={fetchSettings} disabled={saving}>
                                <RefreshCw size={15} className="me-1" />Reset
                            </Button>
                            <Button variant="primary" type="submit" disabled={saving} style={{ minWidth:140 }}>
                                {saving
                                    ? <><Spinner size="sm" className="me-1" />Saving…</>
                                    : <><Save size={15} className="me-1" />Save Settings</>}
                            </Button>
                        </div>
                    </Form>
                </Tab>

                {/* ── Tab 2: HR Automation ── */}
                <Tab eventKey="automation" title={<span><Hash size={14} className="me-1" />HR Automation</span>}>
                    <Form noValidate onSubmit={handleSave}>
                        <Card className="border-0 shadow-sm p-4">
                            <h6 className="fw-bold mb-1">Auto Employee Code &amp; Email Settings</h6>
                            <p className="text-muted small mb-4">
                                When you add a new employee, PeoplesDock auto-generates their
                                employee code and work email using these settings.
                            </p>
                            <Row className="g-4">
                                <Col xs={12} md={6}>
                                    <Card className="border p-3 h-100" style={{ borderColor:'#e2e8f0' }}>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <Hash size={18} className="text-primary" />
                                            <span className="fw-bold">Company Code</span>
                                            <Badge bg="primary" style={{ fontSize:10 }}>Auto Employee ID</Badge>
                                        </div>
                                        <Form.Label className="small text-muted mb-1">
                                            Enter 1–6 letters (e.g. <strong>I2S</strong> or <strong>TVS</strong>)
                                        </Form.Label>
                                        <Form.Control
                                            value={form.company_code}
                                            maxLength={6}
                                            placeholder="e.g. I2S"
                                            onChange={e => set('company_code', e.target.value.replace(/[^A-Za-z0-9]/g,'').toUpperCase())}
                                            style={{ textTransform:'uppercase', fontFamily:'monospace', letterSpacing:2 }}
                                        />
                                        {form.company_code && (
                                            <Alert variant="info" className="mt-2 py-1 px-2 small mb-0">
                                                New employees will get codes like:{' '}
                                                <strong>{form.company_code}-001</strong>,{' '}
                                                <strong>{form.company_code}-002</strong>…
                                            </Alert>
                                        )}
                                    </Card>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Card className="border p-3 h-100" style={{ borderColor:'#e2e8f0' }}>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <Mail size={18} className="text-primary" />
                                            <span className="fw-bold">Email Domain</span>
                                            <Badge bg="success" style={{ fontSize:10 }}>Auto Email</Badge>
                                        </div>
                                        <Form.Label className="small text-muted mb-1">
                                            Enter your company email domain (without @)
                                        </Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text className="text-muted">@</InputGroup.Text>
                                            <Form.Control
                                                value={form.company_domain}
                                                placeholder="peoplesdock.com"
                                                onChange={e => set('company_domain', e.target.value.toLowerCase().replace(/^@/,''))}
                                                style={{ fontFamily:'monospace' }}
                                            />
                                        </InputGroup>
                                        {form.company_domain && (
                                            <Alert variant="info" className="mt-2 py-1 px-2 small mb-0">
                                                New employee named <strong>Kiran</strong> will get:{' '}
                                                <strong>kiran@{form.company_domain}</strong>
                                            </Alert>
                                        )}
                                    </Card>
                                </Col>
                                <Col xs={12}>
                                    <Alert variant="light" className="border small">
                                        <strong>How it works:</strong> When you type a new employee's first name
                                        in the Add Employee form, PeoplesDock instantly suggests their employee
                                        code and email address using these settings. You can always override
                                        the suggestions manually before creating the account.
                                    </Alert>
                                </Col>
                            </Row>
                        </Card>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button variant="light" type="button" onClick={fetchSettings} disabled={saving}>
                                <RefreshCw size={15} className="me-1" />Reset
                            </Button>
                            <Button variant="primary" type="submit" disabled={saving} style={{ minWidth:140 }}>
                                {saving
                                    ? <><Spinner size="sm" className="me-1" />Saving…</>
                                    : <><Save size={15} className="me-1" />Save Settings</>}
                            </Button>
                        </div>
                    </Form>
                </Tab>

                {/* ── Tab 3: Branding ── */}
                <Tab eventKey="branding" title={<span><Palette size={14} className="me-1" />Branding</span>}>
                    <Form noValidate onSubmit={handleSave}>
                        <Card className="border-0 shadow-sm p-4">
                            <h6 className="fw-bold mb-1">Colour Scheme</h6>
                            <p className="text-muted small mb-4">
                                Customise the primary and accent colours for your PeoplesDock portal.
                            </p>
                            <Row className="g-4">
                                <Col xs={12} sm={6}>
                                    <Form.Label className="small fw-bold">Primary Colour</Form.Label>
                                    <div className="d-flex gap-2 align-items-center">
                                        <Form.Control type="color" value={form.primary_color}
                                            onChange={e => {
                                                set('primary_color', e.target.value);
                                                document.documentElement.style.setProperty('--primary-color', e.target.value);
                                            }}
                                            style={{ width:48, height:48, padding:2, cursor:'pointer' }} />
                                        <Form.Control value={form.primary_color}
                                            onChange={e => set('primary_color', e.target.value)}
                                            placeholder="#4f46e5"
                                            style={{ fontFamily:'monospace', maxWidth:120 }} />
                                    </div>
                                    <Form.Text className="text-muted">Used for buttons, links, nav highlights</Form.Text>
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Form.Label className="small fw-bold">Secondary / Accent Colour</Form.Label>
                                    <div className="d-flex gap-2 align-items-center">
                                        <Form.Control type="color" value={form.secondary_color}
                                            onChange={e => set('secondary_color', e.target.value)}
                                            style={{ width:48, height:48, padding:2, cursor:'pointer' }} />
                                        <Form.Control value={form.secondary_color}
                                            onChange={e => set('secondary_color', e.target.value)}
                                            placeholder="#7c3aed"
                                            style={{ fontFamily:'monospace', maxWidth:120 }} />
                                    </div>
                                    <Form.Text className="text-muted">Used for gradients and highlights</Form.Text>
                                </Col>
                                <Col xs={12}>
                                    <div className="d-flex gap-2 align-items-center flex-wrap">
                                        <span className="small text-muted">Quick presets:</span>
                                        {[
                                            { name:'Indigo', primary:'#4f46e5', secondary:'#7c3aed' },
                                            { name:'Blue',   primary:'#2563eb', secondary:'#1d4ed8' },
                                            { name:'Green',  primary:'#059669', secondary:'#047857' },
                                            { name:'Rose',   primary:'#e11d48', secondary:'#be123c' },
                                            { name:'Slate',  primary:'#475569', secondary:'#334155' },
                                        ].map(p => (
                                            <button key={p.name} type="button"
                                                onClick={() => {
                                                    set('primary_color', p.primary);
                                                    set('secondary_color', p.secondary);
                                                    document.documentElement.style.setProperty('--primary-color', p.primary);
                                                }}
                                                style={{
                                                    background:p.primary, color:'#fff', border:'none',
                                                    borderRadius:6, padding:'4px 10px', fontSize:12, cursor:'pointer',
                                                    outline: form.primary_color === p.primary ? '2px solid #000' : 'none',
                                                }}>
                                                {p.name}
                                            </button>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button variant="light" type="button" onClick={fetchSettings} disabled={saving}>
                                <RefreshCw size={15} className="me-1" />Reset
                            </Button>
                            <Button variant="primary" type="submit" disabled={saving} style={{ minWidth:140 }}>
                                {saving
                                    ? <><Spinner size="sm" className="me-1" />Saving…</>
                                    : <><Save size={15} className="me-1" />Save Settings</>}
                            </Button>
                        </div>
                    </Form>
                </Tab>

                {/* ── Tab 4: Payroll & Tax — CompanyPayrollSettings has its own Save button ── */}
                <Tab
                    eventKey="payroll"
                    title={<span><Shield size={14} className="me-1" />Payroll &amp; Tax</span>}>
                    {/* CompanyPayrollSettings manages its own state and save — no outer Form needed */}
                    <CompanyPayrollSettings />
                </Tab>

            </Tabs>
        </div>
    );
};

export default BrandingSettings;
